import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, 
  Plus, 
  Play, 
  Pause, 
  Edit3, 
  Trash2, 
  Clock, 
  Mail,
  MessageSquare,
  Phone,
  Calendar,
  Tag,
  Users,
  TrendingUp,
  Settings,
  Copy,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const AutomationPanel = ({ funnelData }) => {
  const [automations, setAutomations] = useState([
    {
      id: 1,
      name: 'Welcome Email Series',
      trigger: 'Lead Magnet Download',
      status: 'active',
      type: 'email',
      steps: 5,
      conversions: 234,
      lastRun: '2025-01-10T10:30:00Z',
      sequence: [
        { type: 'email', title: 'Welcome & Thank You', delay: '0 minutes' },
        { type: 'email', title: 'Value-packed Content', delay: '1 day' },
        { type: 'email', title: 'Case Study', delay: '3 days' },
        { type: 'email', title: 'Social Proof', delay: '5 days' },
        { type: 'email', title: 'Special Offer', delay: '7 days' }
      ]
    },
    {
      id: 2,
      name: 'Abandoned Cart Recovery',
      trigger: 'Cart Abandonment',
      status: 'active',
      type: 'mixed',
      steps: 3,
      conversions: 87,
      lastRun: '2025-01-10T14:15:00Z',
      sequence: [
        { type: 'email', title: 'Did you forget something?', delay: '1 hour' },
        { type: 'sms', title: 'Limited time offer', delay: '1 day' },
        { type: 'email', title: 'Last chance', delay: '3 days' }
      ]
    },
    {
      id: 3,
      name: 'Consultation Booking Flow',
      trigger: 'Form Submission',
      status: 'draft',
      type: 'mixed',
      steps: 4,
      conversions: 156,
      lastRun: null,
      sequence: [
        { type: 'email', title: 'Booking confirmation', delay: '0 minutes' },
        { type: 'calendar', title: 'Schedule reminder', delay: '1 day before' },
        { type: 'sms', title: 'Appointment reminder', delay: '1 hour before' },
        { type: 'email', title: 'Follow-up questionnaire', delay: '1 day after' }
      ]
    }
  ]);

  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedAutomations, setExpandedAutomations] = useState(new Set());

  const triggers = [
    { id: 'form_submit', label: 'Form Submission', icon: Users },
    { id: 'page_visit', label: 'Page Visit', icon: TrendingUp },
    { id: 'download', label: 'File Download', icon: Mail },
    { id: 'purchase', label: 'Purchase Made', icon: Tag },
    { id: 'cart_abandon', label: 'Cart Abandonment', icon: Clock },
    { id: 'email_click', label: 'Email Link Click', icon: MessageSquare }
  ];

  const actionTypes = [
    { id: 'email', label: 'Send Email', icon: Mail, color: 'bg-blue-500' },
    { id: 'sms', label: 'Send SMS', icon: MessageSquare, color: 'bg-green-500' },
    { id: 'call', label: 'Make Call', icon: Phone, color: 'bg-purple-500' },
    { id: 'calendar', label: 'Calendar Action', icon: Calendar, color: 'bg-orange-500' },
    { id: 'tag', label: 'Add Tag', icon: Tag, color: 'bg-teal-500' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return Mail;
      case 'sms': return MessageSquare;
      case 'call': return Phone;
      case 'calendar': return Calendar;
      case 'mixed': return Zap;
      default: return Zap;
    }
  };

  const toggleAutomation = (id) => {
    setAutomations(prev => prev?.map(automation => 
      automation?.id === id 
        ? { 
            ...automation, 
            status: automation?.status === 'active' ? 'paused' : 'active' 
          }
        : automation
    ));
  };

  const toggleExpanded = (id) => {
    setExpandedAutomations(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(id)) {
        newSet?.delete(id);
      } else {
        newSet?.add(id);
      }
      return newSet;
    });
  };

  const duplicateAutomation = (automation) => {
    const newAutomation = {
      ...automation,
      id: Math.max(...automations?.map(a => a?.id)) + 1,
      name: `${automation?.name} (Copy)`,
      status: 'draft',
      conversions: 0,
      lastRun: null
    };
    setAutomations(prev => [...prev, newAutomation]);
  };

  const deleteAutomation = (id) => {
    setAutomations(prev => prev?.filter(automation => automation?.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Automation Hub</h2>
          <p className="text-gray-600">Create and manage automated workflows for your funnel</p>
        </div>
        
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors mt-4 lg:mt-0"
        >
          <Plus className="w-5 h-5" />
          <span>Create Automation</span>
        </button>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Active Automations', value: automations?.filter(a => a?.status === 'active')?.length, icon: Zap, color: 'bg-green-500' },
          { label: 'Total Conversions', value: automations?.reduce((sum, a) => sum + a?.conversions, 0), icon: TrendingUp, color: 'bg-blue-500' },
          { label: 'Automation Rate', value: '94%', icon: Settings, color: 'bg-purple-500' },
          { label: 'Avg Response Time', value: '2.3min', icon: Clock, color: 'bg-orange-500' }
        ]?.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{stat?.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat?.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat?.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Automation List */}
      <div className="space-y-4">
        {automations?.map((automation) => {
          const TypeIcon = getTypeIcon(automation?.type);
          const isExpanded = expandedAutomations?.has(automation?.id);
          
          return (
            <motion.div
              key={automation?.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Automation Header */}
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => toggleExpanded(automation?.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>
                    
                    <div className={`p-3 rounded-lg ${
                      automation?.type === 'email' ? 'bg-blue-500' :
                      automation?.type === 'sms' ? 'bg-green-500' :
                      automation?.type === 'mixed' ? 'bg-purple-500' : 'bg-gray-500'
                    }`}>
                      <TypeIcon className="w-6 h-6 text-white" />
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{automation?.name}</h3>
                      <p className="text-sm text-gray-600">
                        Trigger: {automation?.trigger} • {automation?.steps} steps
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {automation?.conversions} conversions
                      </div>
                      <div className="text-xs text-gray-500">
                        {automation?.lastRun ? (
                          `Last run: ${new Date(automation.lastRun)?.toLocaleDateString()}`
                        ) : (
                          'Never run'
                        )}
                      </div>
                    </div>
                    
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(automation?.status)}`}>
                      {automation?.status}
                    </span>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleAutomation(automation?.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          automation?.status === 'active' ?'text-red-600 hover:bg-red-50' :'text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {automation?.status === 'active' ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      
                      <button
                        onClick={() => duplicateAutomation(automation)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => setSelectedAutomation(automation)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => deleteAutomation(automation?.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Automation Details */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-gray-200 p-6 bg-gray-50"
                  >
                    <h4 className="font-semibold text-gray-900 mb-4">Automation Sequence</h4>
                    <div className="space-y-3">
                      {automation?.sequence?.map((step, index) => {
                        const StepIcon = getTypeIcon(step?.type);
                        return (
                          <div key={index} className="flex items-center space-x-4">
                            <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full border-2 border-gray-300 text-sm font-semibold text-gray-600">
                              {index + 1}
                            </div>
                            <div className={`p-2 rounded-lg ${
                              step?.type === 'email' ? 'bg-blue-100 text-blue-600' :
                              step?.type === 'sms' ? 'bg-green-100 text-green-600' :
                              step?.type === 'calendar'? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'
                            }`}>
                              <StepIcon className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{step?.title}</h5>
                              <p className="text-sm text-gray-600">Delay: {step?.delay}</p>
                            </div>
                            {index < automation?.sequence?.length - 1 && (
                              <div className="w-px h-8 bg-gray-300"></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      {/* Quick Templates */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Templates</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              name: 'Lead Nurture Sequence',
              description: '5-email series to convert leads',
              type: 'email',
              steps: 5
            },
            {
              name: 'Webinar Registration Flow',
              description: 'Complete webinar automation',
              type: 'mixed',
              steps: 7
            },
            {
              name: 'Customer Onboarding',
              description: 'Welcome new customers',
              type: 'mixed',
              steps: 4
            }
          ]?.map((template, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
            >
              <div className="mb-3">
                <Zap className="w-8 h-8 text-purple-600 mx-auto" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{template?.name}</h4>
              <p className="text-sm text-gray-600 mb-3">{template?.description}</p>
              <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                {template?.steps} steps
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AutomationPanel;